import React from 'react';
import { cn } from '../../utils/cn';
import { t } from '../../utils/translations';
import { IconButton } from './Button';
import { actionIcon } from './actionIcons';
import ActionMenu from './ActionMenu';

export interface RowActionsProps {
  /** Omit to hide the action — used for permission gating. */
  onEdit?: () => void;
  onDelete?: () => void;
  /** Extra actions rendered before the edit/delete group, e.g. "add link" on a folder. */
  leading?: React.ReactNode;
  /** Blocks the actions while a save is in flight. */
  disabled?: boolean;
  editLabel?: string;
  deleteLabel?: string;
  className?: string;
}

/**
 * The edit/delete affordance attached to a row or card.
 *
 * One rule, applied everywhere: both actions collapse behind a "⋮" menu, a lone action stays a
 * standalone icon button. Deciding here rather than at each call site is what keeps the app from
 * drifting back into a mix of inline pairs and dropdowns for the same two actions.
 */
const RowActions: React.FC<RowActionsProps> = ({
  onEdit,
  onDelete,
  leading,
  disabled = false,
  editLabel,
  deleteLabel,
  className,
}) => {
  if (!onEdit && !onDelete && !leading) return null;

  const wrapper = (children: React.ReactNode) => (
    <div className={cn('flex shrink-0 items-center gap-0.5', className)}>
      {leading}
      {children}
    </div>
  );

  if (onEdit && onDelete) {
    return wrapper(
      <ActionMenu
        items={[
          {
            label: editLabel ?? t.common.edit,
            icon: actionIcon.edit,
            onClick: onEdit,
          },
          {
            label: deleteLabel ?? t.common.delete,
            icon: actionIcon.delete,
            tone: 'danger',
            onClick: onDelete,
          },
        ]}
      />
    );
  }

  if (onEdit) {
    return wrapper(
      <IconButton
        icon={actionIcon.edit}
        label={editLabel ?? t.common.edit}
        onClick={onEdit}
        disabled={disabled}
      />
    );
  }

  if (onDelete) {
    return wrapper(
      <IconButton
        icon={actionIcon.delete}
        tone="danger"
        label={deleteLabel ?? t.common.delete}
        onClick={onDelete}
        disabled={disabled}
      />
    );
  }

  return wrapper(null);
};

export default RowActions;
